<template>
    <div class="auth-step">
        <button type="button" class="auth-step__back" @click="$emit('back')">
            <v-icon icon="mdi-arrow-left" size="18" class="auth-step__back-icon" />
            Back
        </button>

        <h1 class="app-typography-title-1 auth-step__title">Enter your credentials</h1>

        <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-4 rounded-lg">
            {{ errorMsg }}
        </v-alert>

        <v-form ref="formRef" @submit.prevent="submitLogin">
            <label class="auth-step__label">Username</label>
            <v-text-field
                v-model="email"
                placeholder="@username"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="auth-step__field mb-4"
                :rules="[rules.required]"
            />

            <label class="auth-step__label">Password</label>
            <v-text-field
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="@#*%"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="auth-step__field mb-4"
                :rules="[rules.required]"
                :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="showPassword = !showPassword"
            />

            <div class="auth-step__row mb-6">
                <v-checkbox
                    v-model="rememberMe"
                    label="Remember me"
                    color="primary"
                    density="compact"
                    hide-details
                    class="auth-step__remember"
                />
                <button type="button" class="auth-step__link" @click="$emit('forgotPassword')">
                    Forgot Password?
                </button>
            </div>

            <v-btn
                block
                color="primary"
                class="text-none app-btn-primary"
                type="submit"
                :loading="loading"
                elevation="0"
            >
                Login
            </v-btn>
        </v-form>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

defineProps<{
    errorMsg?: string
    loading?: boolean
}>()

const emit = defineEmits<{
    logonDetails: [credentials: { email: string; password: string }]
    forgotPassword: []
    back: []
}>()

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null)
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)

const rules = {
    required: (v: string) => !!v?.trim() || 'This field is required',
}

async function submitLogin() {
    const { valid } = await formRef.value!.validate()
    if (valid) {
        emit('logonDetails', { email: email.value, password: password.value })
    }
}
</script>

<style scoped>
.auth-step__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0 0 20px;
    padding: 0;
    border: none;
    background: transparent;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
}

.auth-step__back:hover {
    opacity: 0.85;
}

.auth-step__back-icon {
    color: rgb(var(--v-theme-primary));
}

.auth-step__title {
    margin: 0 0 24px;
}

.auth-step__label {
    display: block;
    margin-bottom: 8px;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-neutral));
}

.auth-step__field :deep(.v-field) {
    border-radius: 8px;
}

.auth-step__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.auth-step__remember :deep(.v-label) {
    font-size: 14px;
    color: rgb(var(--v-theme-heading));
    opacity: 1;
}

.auth-step__link {
    padding: 0;
    border: none;
    background: transparent;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
}

.auth-step__link:hover {
    text-decoration: underline;
}
</style>
